import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FiUpload, FiX } from 'react-icons/fi';
import { getUnblockWebImageUrl } from '../utils/unblockWebAssets';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * Drag & drop + file picker for a single unblock web config image (R2 unblock/{filename}).
 */
const UnblockWebImageUploadSection = ({
    label = 'Image',
    image = '',
    onImageChange,
    disabled = false,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;

        setError('');
        setUploading(true);
        try {
            const body = new FormData();
            body.append('file', file);
            if (image) {
                body.append('replaceFilename', image);
            }

            const res = await axios.post(
                `${API_BASE}/api/unblock-web-config/image`,
                body,
                {
                    headers: {
                        ...authHeaders(),
                        'Content-Type': 'multipart/form-data',
                    },
                    timeout: 60000,
                }
            );

            if (res.data.success) {
                const filename = res.data.data?.filename || '';
                onImageChange && onImageChange(filename);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!image) return;
        setError('');
        try {
            await axios.delete(`${API_BASE}/api/unblock-web-config/image`, {
                headers: authHeaders(),
                data: { filename: image },
            });
            onImageChange && onImageChange('');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete failed');
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled || uploading) return;
        handleFiles(e.dataTransfer.files);
    };

    const displaySrc = image ? getUnblockWebImageUrl(image) : '';

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div
                onDrop={onDrop}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled && !uploading) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
                } ${disabled || uploading ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:border-blue-400'}`}
                onClick={() => !disabled && !uploading && inputRef.current?.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = '';
                    }}
                />
                <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-1 text-sm text-gray-600">Drag & drop or click to upload image</p>
            </div>
            {uploading && <p className="text-sm text-blue-600">Uploading…</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {displaySrc && (
                <div className="flex flex-wrap gap-2 mt-2">
                    <div className="relative group">
                        <img
                            src={displaySrc}
                            alt={image}
                            className="w-24 h-24 object-cover rounded border border-gray-200"
                        />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove();
                            }}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                            aria-label="Remove image"
                        >
                            <FiX className="w-4 h-4" />
                        </button>
                    </div>
                    <span className="self-center text-xs text-gray-500 truncate max-w-[200px]">{image}</span>
                </div>
            )}
        </div>
    );
};

export default UnblockWebImageUploadSection;
